// barba/barba-prevent-handler.js

/**
 * Determines if Barba.js should prevent handling a link click.
 * @param {object} eventData - The event data object from Barba.
 * @param {HTMLAnchorElement} eventData.el - The clicked anchor element.
 * @returns {boolean} True if Barba should be prevented, false otherwise.
 */
export function barbaPreventHandler({ el }) {
  // Normalize href and target to handle SVG links where these properties are objects (SVGAnimatedString)
  const href = (el && typeof el.href === 'string') ? el.href : (el && el.getAttribute ? el.getAttribute('href') : '') || '';
  const target = (el && typeof el.target === 'string') ? el.target : (el && el.getAttribute ? el.getAttribute('target') : '') || '';

  // If the clicked element or its href is missing, or href is empty, Barba should not handle it.
  if (!el || !href) {
    return true; // Prevent Barba from handling
  }

  // Prevent Barba from handling clicks on top-level menu items that have children.
  // This allows them to act as dropdown toggles without navigating.
  const menuItem = el.closest('li.menu-item-has-children');
  // This condition is now more specific: it only prevents Barba if the clicked link (`el`)
  // is a *direct child* of the `li.menu-item-has-children`. This correctly targets
  // the parent toggle link, while allowing links inside the sub-menu to be handled by Barba.
  if (menuItem && el.parentElement === menuItem) {
    return true; // Prevent Barba for the parent toggle link.
  }

  if (el.closest && el.closest('#wpadminbar')) return true; // WordPress Admin Bar
  if (target === '_blank') return true; // Links opening in a new tab
  if (/\.(pdf|zip|jpg|jpeg|png|gif|xml|svg|docx|xlsx|pptx|mp4|webm|mp3|ogg)$/i.test(href)) return true; // File downloads
  if (el.hasAttribute && el.hasAttribute('data-barba-prevent')) return true; // Explicitly prevented links
  if (href.includes('wp-login.php') || href.includes('logout')) return true; // WP login/logout
  // If Lenis handles all same-page anchors, you might not need to prevent them for Barba.
  // if (el.pathname === window.location.pathname && el.hash && el.hash !== '#') return true;

  return false; // If none of the above, Barba handles it.
}


/**
 * Manually attempts to synchronize the document.body.className with the
 * class attribute found on the <body> tag of the fetched HTML content.
 * This is typically a workaround if Barba.js's `sync: true` option
 * isn't behaving as expected for body classes.
 * @param {string} nextHtml - The full HTML string of the next page.
 * @param {boolean} debug - Whether to log debugging information.
 */
export function syncManualBodyClasses(nextHtml, debug = false) {
  if (debug) {
    console.log('[Barba Debug] syncManualBodyClasses: Attempting manual body class sync.');
  }

  const nextHtmlBodyMatch = nextHtml.match(/<body([^>]*)>/i);
  if (nextHtmlBodyMatch && nextHtmlBodyMatch[1]) {
    const nextHtmlBodyClassMatch = nextHtmlBodyMatch[1].match(/class="([^"]*)"/i);
    if (nextHtmlBodyClassMatch && nextHtmlBodyClassMatch[1]) {
      const fetchedBodyClasses = nextHtmlBodyClassMatch[1];
      if (debug) {
        console.log('[Barba Debug] syncManualBodyClasses: Class attribute of <body> in fetched (next) HTML:', fetchedBodyClasses);
      }
      document.body.className = fetchedBodyClasses;
      if (debug) {
        console.log('[Barba Debug] syncManualBodyClasses: document.body.className AFTER manual sync attempt:', document.body.className);
      }
    } else if (debug) {
      console.warn('[Barba Debug] syncManualBodyClasses: No class attribute found on <body> in fetched (next) HTML.');
    }
  } else if (debug) {
    console.warn('[Barba Debug] syncManualBodyClasses: Could not find <body> tag in fetched (next) HTML.');
  }
}

/**
 * Manages page-specific inline <style> tags for Barba.js transitions.
 * It removes old styles and injects new ones into the <head>.
 * @param {string} nextHtml - The full HTML string of the next page.
 * @param {boolean} debug - Whether to log debugging information.
 */
export function syncPageStyles(nextHtml, debug = false) {
  // 1. Remove any old page-specific styles from the head.
  document.head.querySelectorAll('style[data-barba-page-styles]').forEach(style => {
    style.remove();
    if (debug) console.log('[Barba Debug] syncPageStyles: Removed old page-specific style tag.');
  });

  // 2. Find new page-specific styles in the incoming HTML.
  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = nextHtml;
  const newStyles = tempDiv.querySelectorAll('style[data-barba-page-styles]');

  // 3. Append new styles to the head.
  if (newStyles.length > 0) {
    newStyles.forEach(style => {
      document.head.appendChild(style.cloneNode(true));
      if (debug) console.log('[Barba Debug] syncPageStyles: Added new page-specific style tag to <head>.');
    });
  } else if (debug) {
    console.log('[Barba Debug] syncPageStyles: No new page-specific styles found to add.');
  }
}

/**
 * Adds any <link rel="stylesheet"> from the next page (outside the container) that isn't already
 * loaded. Plugins such as Gravity Forms only enqueue their CSS on pages that use
 * them, and Barba only swaps the container, so these would otherwise be missing.
 * Existing stylesheets are left in place to avoid unstyling the outgoing page.
 * @param {string} nextHtml - The full HTML string of the next page.
 * @param {boolean} debug - Whether to log debugging information.
 * @param {number} timeout - Max time in ms to wait for new stylesheets to load.
 * @returns {Promise<void>} Resolves once new stylesheets have loaded (or timed out).
 */
export function syncHeadStylesheets(nextHtml, debug = false, timeout = 3000) {
  const doc = new DOMParser().parseFromString(nextHtml, 'text/html');
  const existing = Array.from(document.querySelectorAll('link[rel="stylesheet"]'));
  const existingIds = new Set(existing.map(link => link.id).filter(Boolean));
  const existingHrefs = new Set(existing.map(link => link.href));

  const loads = [];

  // Search the whole document, not just <head>: stray output in the head (e.g. PHP
  // notices) makes the parser close it early, and WP can print late styles in the footer.
  doc.querySelectorAll('link[rel="stylesheet"]').forEach(link => {
    if (link.closest('[data-barba="container"]')) return; // Barba inserts these itself
    const href = new URL(link.getAttribute('href'), document.baseURI).href;
    if ((link.id && existingIds.has(link.id)) || existingHrefs.has(href)) return;

    const newLink = link.cloneNode(true);
    loads.push(new Promise(resolve => {
      newLink.addEventListener('load', resolve, { once: true });
      newLink.addEventListener('error', resolve, { once: true });
    }));
    document.head.appendChild(newLink);
    if (debug) console.log('[Barba Debug] syncHeadStylesheets: Added stylesheet:', link.id || href);
  });

  if (!loads.length) return Promise.resolve();

  return Promise.race([
    Promise.all(loads),
    new Promise(resolve => setTimeout(resolve, timeout)),
  ]).then(() => undefined);
}

/**
 * Finds, removes, and injects page-specific scripts for specified locations.
 * This function correctly handles script re-execution by creating new script elements.
 *
 * @param {string} nextHtml - The full HTML string of the next page.
 * @param {string[]} locations - An array of locations to process (e.g., ['head', 'body-start']).
 * @param {boolean} debug - Whether to log debugging information.
 */
export function syncPageScriptsByLocation(nextHtml, locations, debug = false) {
  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = nextHtml;

  locations.forEach(location => {
    const selector = `[data-barba-page-script="${location}"]`;

    // 1. Remove old scripts/tags from the live DOM
    document.querySelectorAll(selector).forEach(el => {
      el.remove();
      if (debug) console.log(`[Barba Debug] syncScripts: Removed old element from "${location}":`, el.tagName);
    });

    // 2. Find the new script/tag elements in the incoming HTML
    const newElements = tempDiv.querySelectorAll(selector);

    if (newElements.length > 0) {
      let targetElement;
      let insertBefore = null;

      if (location === 'head') {
        targetElement = document.head;
      } else if (location === 'body-start') {
        targetElement = document.body;
        insertBefore = document.body.firstChild;
      } else { // 'body-end'
        targetElement = document.body;
      }

      // 3. Clone, re-create (for execution), and inject each new element
      newElements.forEach(element => {
        let newEl;

        // Scripts need to be re-created to execute. Simply appending a cloned script tag won't run it.
        if (element.tagName === 'SCRIPT') {
          newEl = document.createElement('script');
          // Copy all attributes from the original script to the new one
          Array.from(element.attributes).forEach(attr => newEl.setAttribute(attr.name, attr.value));
          // Copy the content (for inline scripts)
          newEl.textContent = element.textContent;
        } else {
          // For other tags like <link>, <style>, <meta>, cloning is sufficient.
          newEl = element.cloneNode(true);
        }

        // 4. Inject the new element into the correct position
        if (location === 'body-start' && insertBefore) {
          targetElement.insertBefore(newEl, insertBefore);
        } else {
          targetElement.appendChild(newEl);
        }
      });
      if (debug) console.log(`[Barba Debug] syncScripts: Injected ${newElements.length} new element(s) into "${location}".`);
    }
  });
}

/**
 * Manages page-specific modal windows for Barba.js transitions.
 * It removes old modals and injects new ones into the <body>.
 * @param {string} nextHtml - The full HTML string of the next page.
 * @param {boolean} debug - Whether to log debugging information.
 */
export function syncPageModals(nextHtml, debug = false) {
  // 1. Remove any old page-specific modals from the body.
  document.querySelectorAll('[data-barba-page-modal]').forEach(modal => {
    modal.remove();
    if (debug) console.log('[Barba Debug] syncPageModals: Removed old page-specific modal:', modal.id);
  });

  // 2. Find new page-specific modals in the incoming HTML.
  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = nextHtml;
  const newModals = tempDiv.querySelectorAll('[data-barba-page-modal]');

  // 3. Append new modals to the body.
  if (newModals.length > 0) {
    newModals.forEach(modal => {
      document.body.appendChild(modal.cloneNode(true));
      if (debug) console.log('[Barba Debug] syncPageModals: Added new page-specific modal to <body>:', modal.id);
    });
  } else if (debug) {
    console.log('[Barba Debug] syncPageModals: No new page-specific modals found to add.');
  }
}

/**
 * Fades out an element.
 * @param {HTMLElement} element - The element to fade out.
 * @param {number} duration - The duration of the fade in milliseconds.
 * @returns {Promise<void>} A promise that resolves when the animation is complete.
 */
export async function fadeOut(element, duration = 300) {
  return new Promise(resolve => {
    if (!element) {
      resolve();
      return;
    }
    element.style.transition = `opacity ${duration / 1000}s ease-out`;
    element.style.opacity = '0';
    setTimeout(resolve, duration);
  });
}

/**
 * Fades in an element.
 * @param {HTMLElement} element - The element to fade in.
 * @param {number} duration - The duration of the fade in milliseconds.
 * @returns {Promise<void>} A promise that resolves when the animation is complete.
 */
export async function fadeIn(element, duration = 300) {
  return new Promise(resolve => {
    if (!element) {
      resolve();
      return;
    }
    // Opacity should be set to 0 before calling fadeIn, typically in beforeEnter or CSS.
    element.style.transition = `opacity ${duration / 1000}s ease-in`;
    element.style.opacity = '1';
    setTimeout(resolve, duration);
  });
}
